import combineReducers from 'combine-reducers-global-state/lib';
import uniq from 'lodash/uniq';
import user from './user';
import allFilters from './allFilters';
import tickets from './tickets';
import { selectedHistoricalTicketId, lockedConversationId, selectedTicketId } from './selectedTicketId';
import {
  UPDATE_OWN_AVAILABILITY,
  ADD_UNREAD_NOMINATED_TICKET_ID,
  SELECT_NOMINATED_TICKET,
  MOVE_TICKET,
  REMOVE_TICKET,
  START_ONLOAD_LIVE_QUERY,
  LOAD_LONG_TRANSACTION,
  LIVE_QUERY_OFF,
  DO_PRE_LOGIN,
  DO_PRE_LOGOUT,
  AUTH_ERROR,
  CLEAR_AUTH_ERROR,
  SET_ALL_FILTERS,
  UPDATE_FILTER,
  CLEAR_ALL_FILTERS,
  APPLY_SORT_ORDER,
} from '../actions/types';
import conversation from './conversation';
import tools from './tools/index';
import ui from './ui/index';
import longTransactions from './longTransactions';
import { extractNonHistoricalTicketIdFromState } from '../extractFromState';
import { TICKET_STATUS_FILTER } from '../constants/ticketStatuses';
import { FILTER_FIELDS } from '../constants/filterFields';
import businesses from './businesses';
import experts from './experts';
import reduceReducers from 'reduce-reducers';
import partnerTags from './partnerTags';
import couponCodes from './couponCodes';
import forwardToExpert from './forwardToExpert';
import notifications from './notifications';
import abTesting from './abTesting';
import inProgressTreeAndWizardLocation from './inProgressTreeAndWizardLocation';
import escalatedTabFilters from './escalatedTabFilters';
import { isLiveChat } from 'util/chat';
import mlData from './ml';
import parseConfig from './parseConfig';
import sensitiveData from './sensitiveData';
import previousSelectedTicketId from './previousSelectedTicketId';
import { updateExpertLens } from 'reducers/experts';
import copilotWidget from './copilotWidget';
import braintrust from './braintrust';

export const authReducer = (
  state = {
    preLoginEmail: null,
    ssoConfig: null,
    user: null,
    error: null,
  },
  { type, payload }
) => {
  switch (type) {
    case DO_PRE_LOGIN:
      return {
        ...state,
        preLoginEmail: payload.email,
        ssoConfig: payload.ssoConfig,
      };
    case DO_PRE_LOGOUT:
      return {
        ...state,
        preLoginEmail: null,
        ssoConfig: null,
      };
    case AUTH_ERROR:
      return {
        ...state,
        authError: payload.errorMsg,
      };
    case CLEAR_AUTH_ERROR:
      return {
        ...state,
        authError: null,
      };
    default:
      return state;
  }
};

export const liveQueryStarted = (state = false, action) => {
  switch (action.type) {
    case START_ONLOAD_LIVE_QUERY:
      return action.liveQueryStarted;
    case LOAD_LONG_TRANSACTION:
      return action.liveQueryStarted;
    case LIVE_QUERY_OFF:
      return false;
    default:
      return state;
  }
};

// NOTE(vlad|SPD-14347|2026-03-03): Thin proxy reducer — mirrors ticketStatus from allFilters for backwards compat.
// External consumers (QueueContainer, creatorsAsync, liveQueryMiddleware) still read state.selectedTicketStatus.
const selectedTicketStatus = (state = TICKET_STATUS_FILTER.all, action) => {
  switch (action.type) {
    case SET_ALL_FILTERS:
      return action.payload.ticketStatus || state;
    case UPDATE_FILTER:
      if (action.field === FILTER_FIELDS.TICKET_STATUS) {
        return action.value;
      }

      return state;
    case CLEAR_ALL_FILTERS:
      return TICKET_STATUS_FILTER.all;
    default:
      return state;
  }
};

const appliedSortOrder = (state = null, action) => {
  switch (action.type) {
    case APPLY_SORT_ORDER:
      return action.value;
    case CLEAR_ALL_FILTERS:
      return null;
    default:
      return state;
  }
};

// NOTE(vlad|SPD-14347|2026-03-03): Thin proxy reducer — mirrors dateRange from allFilters for backwards compat.
// External consumer: allLiveQueryGraphQL reads state.dateRange.
const dateRangeDefault = { startDate: null, endDate: null, startTime: null, endTime: null };
const dateRange = (state = dateRangeDefault, action) => {
  switch (action.type) {
    case SET_ALL_FILTERS:
      return action.payload.dateRange || state;
    case UPDATE_FILTER:
      if (action.field === FILTER_FIELDS.DATE_RANGE) {
        return action.value;
      }

      return state;
    case CLEAR_ALL_FILTERS:
      return { startDate: null, endDate: null, startTime: null, endTime: null };
    default:
      return state;
  }
};

const unreadNominatedTicketIds = (state = [], action) => {
  switch (action.type) {
    case ADD_UNREAD_NOMINATED_TICKET_ID:
      return uniq([...state, action.ticketId]);
    case SELECT_NOMINATED_TICKET:
      return state.filter((id) => action.selectedTicketId !== id);
    case MOVE_TICKET:
      return state.filter((id) => action.conversationObjectId !== id);
    case REMOVE_TICKET:
      return state.filter((id) => action.ticketId !== id);
    default:
      return state;
  }
};

// immutable setter
const showToolsLens = (state, value) => ({
  ...state,
  ui: {
    ...state.ui,
    tools: {
      ...state.ui.tools,
      general: {
        ...state.ui.tools.general,
        showTools: value,
      },
    },
  },
});

// eslint-disable-next-line
const setShowToolsSelectedTicketAndInbox = ({ state, showTools, selectedTicketId }) => ({
  ...state,
  ui: {
    ...state.ui,
    tools: {
      ...state.ui.tools,
      general: {
        ...state.ui.tools.general,
        showTools: showTools,
      },
    },
  },
  selectedTicketId,
});

// for actions that require different parts of the state
export const topLevelReducer = (state, action) => {
  const isCurrentTicket = (ticketId) => extractNonHistoricalTicketIdFromState(state) === ticketId;
  switch (action.type) {
    case MOVE_TICKET:
      if (isCurrentTicket(action.conversationObjectId)) {
        // Allows taking a ticket while the Actions page remains open
        return setShowToolsSelectedTicketAndInbox({
          state,
          showTools: !isLiveChat(action.communicationType),
          inboxTab: 'assigned',
          selectedTicketId: action.conversationObjectId,
        });
      }

      return state;

    case REMOVE_TICKET:
      if (isCurrentTicket(action.ticketId)) {
        return showToolsLens(state, false);
      }

      return state;

    case UPDATE_OWN_AVAILABILITY:
      if (!state.user.id) {
        throw new Error('must know expert id');
      }

      return {
        ...state,
        experts: updateExpertLens(state.experts, state.user.id, {
          availability: action.availability,
        }),
      };

    default:
      return state;
  }
};

export default reduceReducers(
  null,
  combineReducers({
    liveQueryStarted,
    longTransactions,
    user,
    experts,
    forwardToExpert,
    selectedTicketStatus,
    appliedSortOrder,
    tickets,
    selectedTicketId,
    previousSelectedTicketId,
    selectedHistoricalTicketId,
    conversation,
    tools,
    inProgressTreeAndWizardLocation,
    ui,
    businesses,
    dateRange,
    allFilters,
    partnerTags,
    couponCodes,
    unreadNominatedTicketIds,
    abTesting,
    notifications,
    escalatedTabFilters,
    lockedConversationId,
    mlData,
    parseConfig,
    sensitiveData,
    authReducer,
    copilotWidget,
    braintrust,
  }),
  topLevelReducer
);
